/**
 * SortableCurrencyRow Component
 * 
 * Displays a currency row with drag-and-drop support.
 */

import { ReactElement } from "react";
import {
  Box,
  Typography,
  IconButton,
  TableCell,
  TableRow,
  Chip,
  Tooltip,
  Paper,
  Stack,
} from "@mui/material";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Currency } from "../../../types";
import RefreshIconButton from "../../../components/RefreshIconButton";
import { UseCurrencyHelpersReturn } from "../hooks/useCurrencyHelpers";
import { UseCurrencyHandlersReturn } from "../hooks/useCurrencyHandlers";
import { getDefaultDecimalPlaces } from "../../../utils/currencyDefaults";

export interface SortableCurrencyRowProps {
  currency: Currency;
  defaultCurrency: string;
  helpers: UseCurrencyHelpersReturn;
  handlers: UseCurrencyHandlersReturn;
  index: number;
  totalCurrencies: number;
  isMobile?: boolean;
  canAddCurrency?: boolean;
  canChangeCurrencyRateDecimalPlaces?: boolean;
}

export default function SortableCurrencyRow({
  currency,
  defaultCurrency,
  helpers,
  handlers,
  index,
  totalCurrencies,
  isMobile = false,
  canAddCurrency = true,
  canChangeCurrencyRateDecimalPlaces = true,
}: SortableCurrencyRowProps): ReactElement {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: currency.code });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  if (isMobile) {
    const canDelete = canAddCurrency && !helpers.isCurrencyInUse(currency.code) && currency.code !== defaultCurrency;
    return (
      <Paper
        ref={setNodeRef}
        variant="outlined"
        sx={{
          p: 1,
          ...style,
          borderColor: isDragging ? "primary.main" : "divider",
          cursor: isDragging ? "grabbing" : "grab",
        }}
      >
        <Stack spacing={0.5}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 0.5, flexWrap: "wrap" }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                minWidth: 0,
                touchAction: "none",
              }}
              {...attributes}
              {...listeners}
            >
              <DragIndicatorIcon fontSize="small" sx={{ color: "text.secondary", flexShrink: 0, fontSize: 18 }} />
              <Typography variant="body2" fontWeight="600">
                {currency.code}
              </Typography>
              {currency.code === defaultCurrency && (
                <Chip icon={<CheckCircleIcon />} label="Default" color="primary" size="small" sx={{ height: 20, "& .MuiChip-label": { px: 0.75, fontSize: "0.7rem" } }} />
              )}
              {currency.manuallyEdited && (
                <Typography variant="caption" color="text.secondary" sx={{ fontStyle: "italic", fontSize: "0.7rem" }}>
                  (edited)
                </Typography>
              )}
            </Box>
            {canAddCurrency && (
              <Tooltip title={!canDelete ? (currency.code === defaultCurrency ? "Set another as default first" : "In use") : "Delete"}>
                <span>
                  <IconButton size="small" onClick={() => handlers.handleDeleteClick(currency.code)} disabled={!canDelete} color="error" sx={{ p: 0.25 }}>
                    <DeleteIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem", lineHeight: 1.3 }}>
            {currency.name} ({currency.symbol})
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: "100%" }}>
            <Typography variant="caption" color={currency.rate === null ? "text.secondary" : "text.primary"} sx={{ fontSize: "0.75rem", minWidth: 140 }}>
              {currency.rate === null
                ? `1 ${currency.code} = — ${defaultCurrency}`
                : `1 ${currency.code} = ${helpers.formatRate(1 / currency.rate)} ${defaultCurrency}`}
            </Typography>
            {canChangeCurrencyRateDecimalPlaces && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0, flexShrink: 0 }}>
                <RefreshIconButton
                  onClick={() => handlers.handleRefreshRateClick(currency)}
                  disabled={!currency.manuallyEdited}
                  disabledTooltip="Only manually edited rates can be refreshed"
                  tooltip="Update from API"
                />
                <IconButton size="small" onClick={() => handlers.handleEditRate(currency)} title="Edit rate" sx={{ p: 0.25 }}>
                  <EditIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Box>
            )}
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 0.25 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem" }}>
              Decimals: {currency.decimalPlaces ?? getDefaultDecimalPlaces(currency.code)} · Updated: {helpers.formatDate(currency.updatedAt)}
            </Typography>
            {canChangeCurrencyRateDecimalPlaces && (
              <Tooltip title="Edit decimal places">
                <IconButton size="small" onClick={() => handlers.handleEditDecimalPlaces(currency)} sx={{ p: 0.25 }}>
                  <EditIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Stack>
      </Paper>
    );
  }

  return (
    <TableRow
      ref={setNodeRef}
      style={style}
      key={currency.code}
      hover
      sx={{
        cursor: isDragging ? "grabbing" : "grab",
      }}
    >
      <TableCell>
        <Box
          {...attributes}
          {...listeners}
          sx={{
            display: "flex",
            alignItems: "center",
            cursor: isDragging ? "grabbing" : "grab",
            color: "text.secondary",
            "&:hover": { color: "text.primary" },
          }}
        >
          <DragIndicatorIcon fontSize="small" />
        </Box>
      </TableCell>
      <TableCell>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Typography variant="body2" fontWeight="medium">
            {currency.code}
          </Typography>
          {currency.manuallyEdited && (
            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: "italic" }}>
              (edited)
            </Typography>
          )}
        </Box>
        <Typography variant="caption" color="text.secondary">
          {currency.name} ({currency.symbol})
        </Typography>
      </TableCell>
      <TableCell sx={{ minWidth: 260 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, width: "100%" }}>
          <Typography variant="body2" color={currency.rate === null ? "text.secondary" : "text.primary"} sx={{ minWidth: 175 }}>
            {currency.rate === null
              ? `1 ${currency.code} = — ${defaultCurrency}`
              : `1 ${currency.code} = ${helpers.formatRate(1 / currency.rate)} ${defaultCurrency}`}
          </Typography>
          {canChangeCurrencyRateDecimalPlaces && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0, flexShrink: 0 }}>
              <IconButton
                size="small"
                onClick={() => handlers.handleEditRate(currency)}
                title="Edit rate manually"
              >
                <EditIcon fontSize="small" />
              </IconButton>
              <RefreshIconButton
                onClick={() => handlers.handleRefreshRateClick(currency)}
                disabled={!currency.manuallyEdited}
                disabledTooltip="This currency has not been manually edited. Only manually edited currencies can be refreshed from the API."
                tooltip="Update rate from API"
              />
            </Box>
          )}
        </Box>
      </TableCell>
      <TableCell>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Typography variant="body2">
            {currency.decimalPlaces ?? getDefaultDecimalPlaces(currency.code)}
          </Typography>
          {canChangeCurrencyRateDecimalPlaces && (
            <Tooltip title="Edit decimal places">
              <IconButton
                size="small"
                onClick={() => handlers.handleEditDecimalPlaces(currency)}
                sx={{ ml: 0.5 }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </TableCell>
      <TableCell>
        <Typography variant="body2" color="text.secondary">
          {helpers.formatDate(currency.updatedAt)}
        </Typography>
      </TableCell>
      <TableCell>
        {currency.code === defaultCurrency ? (
          <Chip
            icon={<CheckCircleIcon />}
            label="Default"
            color="primary"
            size="small"
          />
        ) : (
          <Typography variant="body2" color="text.secondary">
            —
          </Typography>
        )}
      </TableCell>
      <TableCell align="right">
        {canAddCurrency && (
          <Tooltip
            title={
              helpers.isCurrencyInUse(currency.code) ? (
                <Box>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    Can't delete currency while it's used in account or card.
                  </Typography>
                  <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                    This currency is used in:
                  </Typography>
                  {helpers.getAccountsUsingCurrency(currency.code).map((acc) => (
                    <Typography key={acc.id} variant="caption" display="block">
                      • {acc.accountName} ({acc.group})
                    </Typography>
                  ))}
                  <Typography variant="caption" sx={{ mt: 0.5, display: "block" }}>
                    Remove or change currency from all accounts to delete.
                  </Typography>
                </Box>
              ) : currency.code === defaultCurrency ? (
                "Can't delete the default currency. Please set another currency as default first."
              ) : (
                ""
              )
            }
            arrow
          >
            <span>
              <IconButton
                size="small"
                onClick={() => handlers.handleDeleteClick(currency.code)}
                disabled={helpers.isCurrencyInUse(currency.code) || currency.code === defaultCurrency}
                color="error"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </TableCell>
    </TableRow>
  );
}
